import axiosInstance from '../axiosInstance'

// Models
import { Site, SiteID } from '../sites/models/site'
import { Employee, EmployeeID, EmployeeJson } from './models/employee'
import { Pagination } from '../common/models/pagination'
import { BuildingID } from '../buildings/models/building'
import { AreaID } from '../areas/models/area'
import { DateTime } from 'luxon'
import { ShiftID } from '../shifts/models/shift'
import { EmployeeShiftOffer, EmployeeShiftOfferDto, employeeShiftOfferFromDto } from '../employee_shift/models/employeeShiftOffer'
import { EmployeeArchiveCheck, EmployeeArchiveCheckDto, employeeArchiveCheckFromDto } from './models/employeeArchiveCheck'
import { GroupID } from '../groups/models/group'

export type EmployeeSortBy = 'last_active_at'

export type HasNewAppLoginFilter = 'yes' | 'no'
export type LastActiveStatus = 'known' | 'unknown' | 'never'

export interface EmployeeSearchForm {
    name?: string
    skillId?: string
    buildingId?: BuildingID
    groupId?: GroupID
    role?: string
    hasAccount?: 'all' | 'yes' | 'no'

    hasNewAppLogin?: HasNewAppLoginFilter

    lastActiveStatus?: LastActiveStatus

    showArchived?: boolean

    sortBy?: EmployeeSortBy | null
    sortDirection?: 'asc' | 'desc'

    perPage?: number
    page?: number

    status?: 'all' | 'applied' | 'pending'
}

interface EmployeeSearchParams {
    name?: string
    role?: string
    skill_id?: string
    building_id?: string
    group_id?: string
    has_account?: 'all' | 'yes' | 'no'

    has_new_app_login?: 0 | 1

    last_active_status?: LastActiveStatus

    show_archived?: boolean

    sort_by?: EmployeeSortBy
    sort_direction?: 'asc' | 'desc'

    per_page: number
    page: number
    includes?: ('employee_skills' | 'employee_skills.skill' | 'employee_skills.skill.permissions' | 'user')[]

    status?: 'applied' | 'pending'
}

export const fetch = async (siteId: SiteID, form: EmployeeSearchForm): Promise<{ employees: Employee[], pagination: Pagination }> => {
    const params: EmployeeSearchParams = {
        sort_by: form.sortBy ?? undefined,
        sort_direction: form.sortDirection ?? 'desc',
        per_page: form.perPage ?? 15,
        page: form.page ?? 1,
    }

    const name = form.name?.trim() ?? ''
    if (name.length > 0) {
        params.name = name
    }

    if (form.buildingId) {
        params.building_id = form.buildingId
    }

    if (form.skillId) {
        params.skill_id = form.skillId
    }

    if (form.groupId) {
        params.group_id = form.groupId
    }

    if (form.role) {
        params.role = form.role
    }

    if (form.hasAccount) {
        params.has_account = form.hasAccount
    }

    if (form.showArchived) {
        params.show_archived = form.showArchived
    }

    if (form.hasNewAppLogin) {
        params.has_new_app_login = form.hasNewAppLogin === 'yes' ? 1 : 0
    }

    if (form.lastActiveStatus) {
        params.last_active_status = form.lastActiveStatus
    }

    params.includes = ['employee_skills.skill.permissions', 'user']

    const response = await axiosInstance.get(`api/orgs/${siteId}/employees`, { params })

    const data = response.data.data as EmployeeJson[]

    return {
        employees: data.map((employee) => {
            return Employee.fromJson(employee as unknown as EmployeeJson)
        }),
        pagination: new Pagination(response.data.meta.pagination)
    }
}

export const show = async (site: Site, employeeId: EmployeeID): Promise<{ employee: Employee }> => {
    const response = await axiosInstance.get(`api/orgs/${site.id}/employees/${employeeId}`)

    const data = response.data.data as EmployeeJson

    return {
        employee: Employee.fromJson(data),
    }
}

export interface EmployeeCreateForm {
    firstName: string
    lastName: string
    email: string
    contactNumber: string
    hireDate?: DateTime

    buildingIds: BuildingID[]
    skillIds: string[]

    role: string | null

    custom_fields?: Record<string, unknown>
}

interface EmployeeCreateData {
    first_name: string
    last_name: string
    email: string
    contact_number: string | null
    hire_date: string | null

    building_ids?: BuildingID[]
    skill_ids: string[]

    role: string | null

    custom_fields?: Record<string, unknown>
}

export const create = async (siteId: SiteID, form: EmployeeCreateForm): Promise<{ employee: Employee }> => {
    const data: EmployeeCreateData = {
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        contact_number: form.contactNumber.trim().length > 0 ? form.contactNumber.trim() : null,
        hire_date: form.hireDate?.toISODate() ?? null,

        skill_ids: form.skillIds,

        role: form.role,

        custom_fields: form.custom_fields,
    }

    const response = await axiosInstance.post(`api/orgs/${siteId}/employees`, data)

    const employeeJsson = response.data.data as EmployeeJson

    return {
        employee: Employee.fromJson(employeeJsson)
    }
}

export interface EmployeeUpdateForm {
    firstName: string
    lastName: string
    email: string
    hireDate: string
    custom_fields?: Record<string, unknown>
}

interface EmployeeUpdateData {
    first_name: string
    last_name: string
    email: string
    hire_date: string | null
    custom_fields?: Record<string, unknown>
}

export const update = async (siteId: SiteID, employeeId: EmployeeID, form: EmployeeUpdateForm): Promise<{ employee: Employee }> => {
    const data: EmployeeUpdateData = {
        first_name: form.firstName,
        last_name: form.lastName,
        email: form.email,
        hire_date: form.hireDate.trim().length > 0 ? form.hireDate.trim() : null,
        custom_fields: form.custom_fields,
    }

    const response = await axiosInstance.patch(`api/orgs/${siteId}/employees/${employeeId}`, data)

    const employeeJsson = response.data.data as EmployeeJson

    return {
        employee: Employee.fromJson(employeeJsson)
    }
}

export interface EmployeeBuildingSyncForm {
    buildingIds: BuildingID[]
}

interface EmployeeBuildingSyncData {
    building_ids: BuildingID[]
}

function syncBuildingsFormToData(form: EmployeeBuildingSyncForm): EmployeeBuildingSyncData {
    return {
        building_ids: form.buildingIds,
    }
}

export const syncBuildings = async (siteId: SiteID, employeeId: EmployeeID, form: EmployeeBuildingSyncForm): Promise<{ employee: Employee }> => {
    const formData = syncBuildingsFormToData(form)

    const response = await axiosInstance.put(`api/orgs/${siteId}/employees/${employeeId}/buildings/memberships`, formData)

    const data = response.data.data as EmployeeJson

    return {
        employee: Employee.fromJson(data),
    }
}

export interface EmployeeAreaSyncForm {
    areaIds: AreaID[]
}

interface EmployeeAreaSyncData {
    area_ids: AreaID[]
}

function syncAreasFormToData(form: EmployeeAreaSyncForm): EmployeeAreaSyncData {
    return {
        area_ids: form.areaIds,
    }
}

export const syncAreas = async (siteId: SiteID, employeeId: EmployeeID, form: EmployeeAreaSyncForm): Promise<{ employee: Employee }> => {
    const formData = syncAreasFormToData(form)

    const response = await axiosInstance.put(`api/orgs/${siteId}/employees/${employeeId}/areas/memberships`, formData)

    const data = response.data.data as EmployeeJson

    return {
        employee: Employee.fromJson(data),
    }
}

export interface EligibleSearchForm {
    shiftId?: ShiftID | null
    areaId?: AreaID | null
    startDate: DateTime | null
    endDate: DateTime | null

    name?: string

    skillId?: string

    groupId?: GroupID | null

    perPage?: number
    page?: number
}

interface EligibleSearchParams {
    shift_id?: ShiftID | null
    area_id?: AreaID | null
    start_date?: string | null
    end_date?: string | null

    group_id?: string | null

    name?: string
    per_page: number
    page: number
}

export const fetchEligible = async (siteId: SiteID, form: EligibleSearchForm): Promise<{ employees: Employee[], pagination: Pagination }> => {
    const params: EligibleSearchParams = {
        area_id: form.areaId,
        start_date: form.startDate?.toISODate(),
        end_date: form.endDate?.toISODate(),

        per_page: form.perPage ?? 15,
        page: form.page ?? 1,
    }

    const name = form.name?.trim() ?? ''
    if (name.length > 0) {
        params.name = name
    }

    if (form.shiftId) {
        params.shift_id = form.shiftId
    }

    if (form.groupId) {
        params.group_id = form.groupId
    }

    const response = await axiosInstance.get(`api/orgs/${siteId}/shifts/employees`, { params })

    const data = response.data.data as EmployeeJson[]

    return {
        employees: data.map((employee) => {
            return Employee.fromJson(employee as unknown as EmployeeJson)
        }),
        pagination: new Pagination(response.data.meta.pagination)
    }
}

export const fetchByShift = async (siteId: SiteID, shiftId: ShiftID, form: EmployeeSearchForm): Promise<{ offers: EmployeeShiftOffer[], pagination: Pagination }> => {
    const params: EmployeeSearchParams = {
        per_page: form.perPage ?? 15,
        page: form.page ?? 1,
    }

    const name = form.name?.trim() ?? ''
    if (name.length > 0) {
        params.name = name
    }

    if (form.status && form.status !== 'all') {
        params.status = form.status
    }

    const response = await axiosInstance.get(`api/orgs/${siteId}/shifts/${shiftId}/offers`, { params })

    const data = response.data.data as EmployeeShiftOfferDto[]

    return {
        offers: data.map((offer) => {
            return employeeShiftOfferFromDto(offer)
        }),
        pagination: new Pagination(response.data.meta.pagination)
    }
}

export const checkArchive = async (siteId: SiteID, employeeId: EmployeeID | null): Promise<{ check: EmployeeArchiveCheck }> => {
    const response = await axiosInstance.get(`api/orgs/${siteId}/employees/${employeeId}/archive`)

    const data = response.data.data as EmployeeArchiveCheckDto

    return {
        check: employeeArchiveCheckFromDto(data)
    }
}

export const archive = async (siteId: SiteID, employeeId: EmployeeID): Promise<void> => {
    await axiosInstance.put(`api/orgs/${siteId}/employees/${employeeId}/archive`)
}

export const restore = async (siteId: SiteID, employeeId: EmployeeID): Promise<void> => {
    await axiosInstance.delete(`api/orgs/${siteId}/employees/${employeeId}/archive`)
}

export interface EmployeeAccountUpdateForm {
    email: string
    contact_number: string
}

interface EmployeeAccountUpdateData {
    email: string
    contact_number?: string
}

export const updateAccount = async (siteId: SiteID, employeeId: EmployeeID, form: EmployeeAccountUpdateForm): Promise<{ employee: Employee }> => {
    const data: EmployeeAccountUpdateData = {
        email: form.email,
    }

    const contactNumber = form.contact_number.trim()
    if (contactNumber.length > 0) {
        data.contact_number = contactNumber
    }

    const response = await axiosInstance.patch(`api/orgs/${siteId}/employees/${employeeId}/account`, data)

    const employeeJsson = response.data.data as EmployeeJson

    return {
        employee: Employee.fromJson(employeeJsson)
    }
}